/* eslint-disable @typescript-eslint/no-use-before-define */
import { LinkButton } from '@euler/app/components/LinkButton';
import { SuspenseContent } from '@euler/app/components/SuspenseContent';
import { TaskTimelineCell } from '@euler/app/flows/task/components/TaskTimeline';
import {
  getTimelineSummaryResourceId,
  useTaskTimelineSummary,
} from '@euler/app/flows/task/functions';
import {
  buildTimeline,
  useTaskTimelineModels,
} from '@euler/app/flows/task/functions/timeline';
import { EventInfo } from '@euler/app/flows/task/functions/timeline/types';
import { usePressTaskTimelineSectionHeader } from '@euler/app/flows/task/functions/usePressTaskTimelineSectionHeader';
import { TaskDetailSection } from '@euler/app/flows/task/pages/detail/components/TaskDetailSection';
import type { TaskNavParams } from '@euler/app/flows/task/TaskFlow';
import { Colors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import {
  LayoutProviderView,
  useContainerLayout,
} from '@euler/components/layout/LayoutProvider';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { dateFromValue } from '@euler/utils/datetime';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { memo, useMemo } from 'react';
import { StyleProp, TextStyle } from 'react-native';
import { useTheme } from 'styled-components';

export const TimelineSummary = memo(
  ({
    taskNo,
    readonly,
    isIndependentDeliveryCheckEnabled,
  }: {
    taskNo: string;
    readonly?: boolean;
    isIndependentDeliveryCheckEnabled: boolean;
  }) => {
    const resourceId = getTimelineSummaryResourceId(taskNo);
    const navigation = useNavigation<StackNavigationProp<TaskNavParams>>();
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    return (
      <TaskDetailSection
        title="服务进度"
        headerRight={'进度详情'}
        onHeaderRightPress={() => {
          navigation.navigate('timeline', { readonly });
        }}
        bodyStyle={{
          paddingLeft: 0,
          paddingRight: 0,
          paddingTop: 0,
          paddingBottom: 0,
        }}
        css={`
          margin-bottom: ${rpx(100)}px;
        `}
      >
        <SuspenseContent resourceId={resourceId}>
          <LayoutProviderView
            css={`
              padding-bottom: ${rpx(15)}px;
            `}
          >
            <Content
              isIndependentDeliveryCheckEnabled={
                isIndependentDeliveryCheckEnabled
              }
            />
            <LinkButton
              text="查看完整进度"
              onPress={() =>
                navigation.navigate('timeline', {
                  readonly,
                })
              }
              css={`
                margin-top: ${rpx(15)}px;
              `}
              textStyle={{ lineHeight: rpx(18) }}
            >
              <Icon
                name="arrow-right-circle"
                size={rpx(18)}
                color={theme.link}
                css={`
                  margin-left: ${rpx(5)}px;
                `}
              />
            </LinkButton>
          </LayoutProviderView>
        </SuspenseContent>
      </TaskDetailSection>
    );
  },
);

const Content = memo(
  ({
    isIndependentDeliveryCheckEnabled,
  }: {
    isIndependentDeliveryCheckEnabled: boolean;
  }) => {
    const { rpx } = useResponsiveContext();
    const timeline = useTaskTimelineSummary();
    const events = useMemo<EventInfo[]>(
      () =>
        timeline.map(x => ({
          id: String(x.id),
          type: x.type,
          subType: x.subType,
          timestamp: dateFromValue(x.timestamp),
          data: x.data ?? undefined,
          dataType: x.dataType,
          dataVersion: x.dataVersion ?? 0,
          author: x.author,
        })),
      [timeline],
    );
    const linkTextStyle = useMemo<StyleProp<TextStyle>>(
      () => ({
        color: Colors.Gray3,
      }),
      [],
    );
    const sections = buildTimeline(events, {
      isIndependentDeliveryCheckEnabled,
    });
    const onPressSectionHeader = usePressTaskTimelineSectionHeader();
    const { cells } = useTaskTimelineModels({
      sections,
      linkTextStyle,
      onPressSectionHeader,
    });
    const { width } = useContainerLayout();
    return (
      <>
        {cells.map(cellModel => (
          <TaskTimelineCell
            key={cellModel.id}
            model={cellModel}
            width={width}
            headerTextStyle={{
              fontSize: rpx(15),
              fontFamily: FontFamily.NotoSans.Light,
            }}
          />
        ))}
      </>
    );
  },
);
